import React, { memo } from 'react';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  Heart,
  MoreHorizontal,
  Shuffle,
  Repeat,
  ListMusic,
  Radio
} from 'lucide-react';

const MusicContent = memo(({ activity }) => {
  const isPlaying = activity?.action === 'play';
  const isChanging = activity?.action === 'next';
  const isScrolling = activity?.action === 'scroll';
  const isClicking = activity?.action === 'click';
  
  const [actionCount, setActionCount] = React.useState(0);
  React.useEffect(() => {
    if (activity) {
      setActionCount(prev => prev + 1);
    }
  }, [activity]);
  
  const showPlaylist = actionCount > 1 && isClicking;
  const selectSong = actionCount > 3 && isClicking;

  const playlists = [
    { name: 'Chill Vibes', color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', tracks: 42 },
    { name: 'Focus Flow', color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', tracks: 28 },
    { name: 'Workout Mix', color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', tracks: 35 },
    { name: 'Evening Jazz', color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', tracks: 51 }
  ];

  const artists = [
    { name: 'Daft Punk', emoji: '🎧' },
    { name: 'Tame Impala', emoji: '🌀' },
    { name: 'ODESZA', emoji: '✨' },
    { name: 'Flume', emoji: '🌊' },
    { name: 'Tycho', emoji: '🌅' }
  ];

  return (
    <div className="flux-music-content">
      {/* Sidebar */}
      <div className="music-sidebar">
        <div className="sidebar-section">
          <div className="sidebar-item active">
            <ListMusic size={8} />
            <span>Listen Now</span>
          </div>
          <div className="sidebar-item">
            <Radio size={8} />
            <span>Radio</span>
          </div>
          <div className="sidebar-item">
            <Heart size={8} />
            <span>Favorites</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`music-main ${isScrolling ? 'scrolling' : ''}`}>
        {/* Header */}
        <div className="music-header">
          <h2 className="music-title">Listen Now</h2>
        </div>

        {/* Recently Played */}
        <div className="music-section">
          <h3 className="section-title">Recently Played</h3>
          <div className="album-grid">
            <div className={`album-card ${isChanging ? 'changing' : ''}`}>
              <div className="album-cover" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                <div className="album-icon">🎵</div>
              </div>
              <div className="album-info">
                <div className="album-name">Discovery</div>
                <div className="album-artist">Daft Punk</div>
              </div>
            </div>
            
            <div className="album-card">
              <div className="album-cover" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }}>
                <div className="album-icon">🎸</div>
              </div>
              <div className="album-info">
                <div className="album-name">Currents</div>
                <div className="album-artist">Tame Impala</div>
              </div>
            </div>

            <div className="album-card">
              <div className="album-cover" style={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' }}>
                <div className="album-icon">🌟</div>
              </div>
              <div className="album-info">
                <div className="album-name">A Moment Apart</div>
                <div className="album-artist">ODESZA</div>
              </div>
            </div>
          </div>
        </div>

        {/* Playlists */}
        <div className="music-section">
          <h3 className="section-title">Your Playlists</h3>
          <div className="playlist-grid">
            {playlists.map((playlist, index) => (
              <div key={index} className={`playlist-card ${index === 0 && showPlaylist ? 'selected' : ''}`}>
                <div className="playlist-cover" style={{ background: playlist.color }}>
                  <div className="playlist-icon">♫</div>
                </div>
                <div className="playlist-info">
                  <div className="playlist-name">{playlist.name}</div>
                  <div className="playlist-tracks">{playlist.tracks} songs</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Playlist Detail View */}
        {showPlaylist && (
          <div className="playlist-detail">
            <h3 className="section-title">Chill Vibes</h3>
            <div className="song-list">
              <div className={`song-item ${selectSong ? 'playing' : ''}`}>
                <span className="song-number">1</span>
                <span className="song-title">Weightless</span>
                <span className="song-artist">Marconi Union</span>
                <span className="song-duration">8:09</span>
              </div>
              <div className="song-item">
                <span className="song-number">2</span>
                <span className="song-title">Electra</span>
                <span className="song-artist">Airstream</span>
                <span className="song-duration">7:15</span>
              </div>
              <div className="song-item">
                <span className="song-number">3</span>
                <span className="song-title">Mellomaniac</span>
                <span className="song-artist">DJ Shah</span>
                <span className="song-duration">5:02</span>
              </div>
            </div>
          </div>
        )}

        {/* Artists */}
        <div className="music-section">
          <h3 className="section-title">Your Artists</h3>
          <div className="artist-row">
            {artists.map((artist, index) => (
              <div key={index} className="artist-card">
                <div className="artist-avatar">{artist.emoji}</div>
                <div className="artist-name">{artist.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Now Playing Bar */}
      <div className="music-player">
        <div className="player-track">
          <div className="player-album-art">
            <div className="mini-album-icon">🎵</div>
          </div>
          <div className="player-track-info">
            <div className="player-track-name">One More Time</div>
            <div className="player-artist-name">Daft Punk</div>
          </div>
          <button className="player-like">
            <Heart size={8} />
          </button>
        </div>

        <div className="player-controls">
          <div className="player-buttons">
            <button className="player-btn small">
              <Shuffle size={6} />
            </button>
            <button className="player-btn">
              <SkipBack size={8} />
            </button>
            <button className={`player-btn play ${isPlaying ? 'playing' : ''}`}>
              {isPlaying ? <Pause size={10} /> : <Play size={10} />}
            </button>
            <button className="player-btn">
              <SkipForward size={8} />
            </button>
            <button className="player-btn small">
              <Repeat size={6} />
            </button>
          </div>
          <div className="player-progress">
            <span className="progress-time">1:23</span>
            <div className={`progress-bar ${isPlaying ? 'playing' : ''}`}>
              <div className="progress-fill" style={{ width: '35%' }}></div>
            </div>
            <span className="progress-time">3:45</span>
          </div>
        </div>

        <div className="player-extras">
          <button className="player-btn small">
            <MoreHorizontal size={8} />
          </button>
          <div className="volume-control">
            <Volume2 size={8} />
            <div className="volume-bar">
              <div className="volume-fill" style={{ width: '60%' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

MusicContent.displayName = 'MusicContent';

export default MusicContent;
